import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronLeft, Info, ChevronRight, User, Send,
  Loader2, Share2, CheckCircle2, Sparkles, Search, Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { getOrCreateReferralCode, getUserReferrals, db } from "@/lib/firebase";
import { collection, getDocs } from "firebase/firestore";
import { Contacts } from "@capacitor-community/contacts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ReferAndEarn() {
  const [isSharing, setIsSharing] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();

  const [referralCode, setReferralCode] = useState("");
  const [successfulReferrals, setSuccessfulReferrals] = useState(0);

  // --- NATIVE CONTACT & FILTER STATES ---
  const [contacts, setContacts] = useState<any[]>([]);
  const [existingUserPhones, setExistingUserPhones] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(25);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  // 1. Fetch referral data AND existing user phone numbers from Firestore
  useEffect(() => {
    async function fetchData() {
      if (!user?.uid) return;
      try {
        const code = await getOrCreateReferralCode(user.uid);
        setReferralCode(code);

        const data = await getUserReferrals(user.uid);
        setSuccessfulReferrals(data.filter(r => r.status === "completed").length);

        // Fetch all registered phone numbers to filter them out of the list
        const usersSnap = await getDocs(collection(db, "users"));
        const phones = new Set<string>();
        usersSnap.forEach(doc => {
          const profile = doc.data().businessProfile;
          const p = profile?.businessPhone || doc.data().phoneNumber;
          if (p) {
            // FIX: Extract ONLY the last 10 digits for a bulletproof comparison
            const digits = p.replace(/\D/g, '');
            if (digits.length >= 10) {
              phones.add(digits.slice(-10));
            }
          }
        });
        setExistingUserPhones(phones);
      } catch (error) {
        console.error("Initialization Error:", error);
      }
    }
    fetchData();
  }, [user?.uid]);

  // 2. Fetch Android Contacts with Firestore exclusion
  const loadAndroidContacts = async () => {
    setIsLoadingContacts(true);
    try {
      const permission = await Contacts.requestPermissions();
      if (permission.contacts !== 'granted') {
        toast({
          title: "Permission Required",
          description: "Please allow contact access in your Android settings to invite friends.",
          variant: "destructive"
        });
        return;
      }

      setHasPermission(true);
      const result = await Contacts.getContacts({ projection: { name: true, phones: true } });

      // Filter logic: Remove duplicates and people already in your database
      const filtered = result.contacts.filter(c => {
        if (!c.phones || c.phones.length === 0) return false;

        // FIX: Extract ONLY the last 10 digits to compare against the database
        const digits = c.phones[0].number.replace(/\D/g, '');
        if (digits.length < 10) return false; // Ignore weird short numbers

        const last10 = digits.slice(-10);
        return !existingUserPhones.has(last10);
      });

      setContacts(filtered);
    } catch (error) {
      console.error("Contact Error:", error);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  // 3. Search Logic
  const filteredContacts = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return contacts.filter(c =>
      c.name?.display?.toLowerCase().includes(term) ||
      c.phones[0].number.includes(term)
    );
  }, [contacts, searchTerm]);

  const inviteLink = `https://dailyhisaab.com/app/invite${referralCode ? `?ref=${referralCode}` : ''}`;
  const shareText = `*Upgrade Your Shop!* 📲✨\n\nStop doing manual billing. 🚫📝 I use *DailyHisaab* for my shop—it makes professional billing and printing receipts via Bluetooth super simple! 🖨️⚡\n\nUse my invite link to download the app 📥🔗 and help me unlock premium rewards! 🎉\n\n${inviteLink}`;

  const handleInvite = (phone: string) => {
    // FIX: Smart Phone Number Formatter for WhatsApp
    let cleanPhone = phone.replace(/\D/g, '');

    // If it's exactly 10 digits (no country code), add India's '91'
    if (cleanPhone.length === 10) {
      cleanPhone = '91' + cleanPhone;
    }
    // If it starts with '0' and is 11 digits (like 09876543210), replace '0' with '91'
    else if (cleanPhone.length === 11 && cleanPhone.startsWith('0')) {
      cleanPhone = '91' + cleanPhone.slice(1);
    }

    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(shareText)}`, "_blank");
  };

  const handleWhatsAppShare = async () => {
    setIsSharing(true);
    try {
      const response = await fetch('/ref-earn-banner.png');
      const blob = await response.blob();
      const file = new File([blob], 'ref-earn-banner.jpg', { type: 'image/jpeg' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "DailyHisaab Invite",
          text: shareText,
          files: [file],
        });
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
      }
    } catch (error) {
      console.log("Sharing failed:", error);
    } finally {
      setIsSharing(false);
    }
  };

  const progressPercentage = Math.min((successfulReferrals / 10) * 100, 100);

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBEB] dark:bg-[#121212]">
      {/* Header */}
      <header className="px-4 py-4 flex items-center justify-between sticky top-0 z-30 bg-[#FFFBEB]/90 dark:bg-[#121212]/90 backdrop-blur-md">
        <button onClick={() => window.history.back()} className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95 transition-transform">
          <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
        </button>
        <button onClick={() => setLocation("/referrals")} className="bg-[#991B1B] hover:bg-[#7f1616] text-white px-4 py-2 rounded-full font-bold text-sm flex items-center shadow-md active:scale-95 transition-all">
          Your Referrals <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </header>

      <div className="flex-1 px-5 pt-2 pb-6 flex flex-col">
        <h1 className="text-3xl font-black text-[#1E293B] dark:text-white tracking-tight leading-tight mb-2">
          Refer Friends,<br />Earn Rewards!
        </h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm font-medium mb-8">
          Invite friends & earn 500 points. Refer 10 friends and get a chance to win a Thermal Bluetooth Printer!
        </p>

        {/* Big Visual Reward Card */}
        <div className="w-full max-w-sm mx-auto bg-gradient-to-br from-[#991B1B] to-[#5c0f0f] rounded-[2rem] shadow-xl relative overflow-hidden flex flex-col mb-6">
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md text-[#991B1B] text-[10px] font-black px-3 py-1 rounded-full uppercase z-20">
            Limited Period
          </div>
          <img src="/ref-earn-banner.png" alt="Banner" className="w-full h-auto object-cover" />
          <div className="p-5 pt-4 relative z-10">
            <div className="h-1.5 w-full bg-black/30 rounded-full overflow-hidden mb-2">
              <div className="h-full bg-white shadow-[0_0_8px_white] transition-all duration-1000" style={{ width: `${progressPercentage}%` }}></div>
            </div>
            <p className="text-center text-sm font-bold text-white/90 uppercase tracking-tight">
              {successfulReferrals} of 10 friends referred
            </p>
          </div>
        </div>

        {/* 🌟 UPGRADED: Referral Code Copy Box */}
        {referralCode && (
          <div className="w-full max-w-sm mx-auto bg-amber-50/50 dark:bg-amber-950/20 border-2 border-dashed border-amber-200 dark:border-amber-900/50 rounded-[1.5rem] flex items-center justify-between p-4 mb-6 shadow-sm relative overflow-hidden">
            <div className="pl-2">
              <p className="text-[10px] font-black text-amber-700/70 dark:text-amber-500/70 uppercase tracking-widest mb-1">
                Your Invite Code
              </p>
              <p className="text-2xl font-black text-[#991B1B] dark:text-red-400 tracking-[0.15em] drop-shadow-sm font-mono">
                {referralCode}
              </p>
            </div>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(referralCode);
                toast({ title: "Code Copied!" });
              }}
              className="h-12 px-4 rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-amber-100 dark:border-slate-700 hover:bg-slate-50 text-[#991B1B] dark:text-red-400 font-bold flex items-center gap-2 active:scale-95 transition-all shrink-0"
            >
              <Copy className="h-4 w-4" />
              Copy
            </Button>
          </div>
        )}

        {/* Global WhatsApp Share Button */}
        <Button
          onClick={handleWhatsAppShare}
          disabled={isSharing || !referralCode}
          className="w-full h-14 bg-white hover:bg-slate-50 text-[#128C7E] rounded-2xl text-lg font-black shadow-sm border border-slate-100 mb-6 active:scale-95 transition-all"
        >
          {isSharing ? <Loader2 className="animate-spin mr-2" /> : <Share2 className="w-6 h-6 mr-2" />}
          Share Invite Link
        </Button>

        {/* Contact List Area with Search */}
        <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-6 flex-1 min-h-[400px]">
          <h3 className="font-bold text-slate-900 dark:text-white text-lg mb-4">Invite from Contacts</h3>

          {!hasPermission ? (
            <Button
              onClick={loadAndroidContacts}
              disabled={!referralCode}
              className="w-full h-14 bg-[#991B1B] text-white rounded-xl text-lg font-bold shadow-md active:scale-[0.98]"
            >
              {isLoadingContacts ? <Loader2 className="animate-spin" /> : "View My Contact List"}
            </Button>
          ) : (
            <div className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name or number..."
                  className="w-full h-11 pl-10 pr-4 rounded-xl bg-slate-50 dark:bg-slate-800 border-none text-sm focus:ring-2 focus:ring-red-100 dark:text-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Scrollable List */}
              <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2">
                {filteredContacts.slice(0, displayLimit).map((contact, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-50 dark:border-slate-800 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-xs text-slate-900 dark:text-white truncate">
                          {contact.name?.display || "Unknown Contact"}
                        </p>
                        <p className="text-[10px] text-slate-500 font-medium">
                          {contact.phones[0].number}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleInvite(contact.phones[0].number)}
                      className="p-2 bg-red-50 dark:bg-red-950/30 rounded-lg active:scale-90 transition-transform"
                    >
                      <Send className="w-4 h-4 text-[#991B1B] dark:text-red-400" />
                    </button>
                  </div>
                ))}

                {filteredContacts.length === 0 && (
                  <p className="text-center text-slate-400 text-xs py-8">No matching contacts found</p>
                )}

                {filteredContacts.length > displayLimit && (
                  <Button
                    variant="ghost"
                    onClick={() => setDisplayLimit(l => l + 25)}
                    className="w-full text-xs text-slate-400 font-bold py-4 hover:bg-transparent"
                  >
                    Load 25 More...
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Persistent Info Link */}
      <button
        onClick={() => setShowHowItWorks(true)}
        className="flex items-center justify-center text-slate-500 text-xs font-bold gap-1.5 py-6 active:opacity-70"
      >
        <Info className="w-4 h-4" /> Know how it works
      </button>

      {/* How it Works Full Notification */}
      <AlertDialog open={showHowItWorks} onOpenChange={setShowHowItWorks}>
        <AlertDialogContent className="rounded-[2.5rem] border-none shadow-2xl max-w-[90%] mx-auto p-0 overflow-hidden bg-white dark:bg-slate-900">
          <div className="p-8 pb-6 text-center">
            <div className="w-16 h-16 bg-amber-50 dark:bg-amber-950/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="h-8 w-8 text-amber-600" />
            </div>
            <AlertDialogTitle className="text-2xl font-bold text-slate-900 dark:text-white mb-4 tracking-tight">
              How it Works
            </AlertDialogTitle>

            <AlertDialogDescription asChild>
              <div className="text-left space-y-5 text-slate-600 dark:text-slate-400">
                <div className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <p><span className="font-bold text-slate-900 dark:text-white">Invite:</span> Share your link with friends who don't use the app yet.</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <p><span className="font-bold text-slate-900 dark:text-white">Activate:</span> Your friend must sign up and record their first sale.</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <p><span className="font-bold text-slate-900 dark:text-white">Rewards:</span> You instantly get <span className="font-bold text-[#991B1B]">500 Points</span> and 1 lucky entry!</p>
                </div>
              </div>
            </AlertDialogDescription>
          </div>
          <div className="px-6 pb-8">
            <AlertDialogAction
              onClick={() => setShowHowItWorks(false)}
              className="w-full py-7 rounded-2xl font-black text-lg text-white shadow-lg active:scale-95 transition-transform"
              style={{ background: "linear-gradient(135deg, #991B1B 0%, #5c0f0f 100%)" }}
            >
              Got it!
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}